import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  MaterialReactTable,
  type MRT_Row as MRTRow,
  type MRT_SortingState as MRTSortingState,
  type MRT_TableInstance as MRTTableInstance,
  type MRT_Updater as MRTUpdater,
} from 'material-react-table';

import { Box } from '@mui/material';
import { alpha, useTheme, Theme, SxProps } from '@mui/material/styles';

import { useSelector } from 'react-redux';
import { RenderNestedComponent, ChildMap, FilterByColumn } from '../../types';

import { useDataGridFlexSales } from './DataGridFlexSalesProvider';
import type { Row as CustomRow } from './DataGridHandler';

import {
  VirtualTableNoDataCell,
  RowLink,
  buildColumns,
  Export,
  renderNestedTable,
} from './dataGridCustom';

import { TablePaginationBar } from '../Pagination';

type RowData = Record<string, any>;

const PAGE_SIZE = 25;
const NO_EXPAND_FIRST_COL_PADDING_PX = 5;

const getBodyRowProps = (
  row: MRTRow<RowData>,
  isDetailPanel: boolean,
  hiddenColumns: Set<string>
) => {
  const isExpanded = row.getIsExpanded() ?? false;

  const baseSx: SxProps<Theme> = {
    mt: 0,
    mb: 0,
    backgroundColor: (theme: Theme) => theme.palette.background.paper,
  };

  if (isDetailPanel) {
    return {
      sx: {
        ...baseSx,
        display: isExpanded ? 'table-row' : 'none',
        height: isExpanded ? 'auto' : 0,
        minHeight: 0,
        maxHeight: isExpanded ? 'none' : 0,
        overflow: 'hidden',
      },
    };
  }

  return {
    sx: {
      ...baseSx,
    },
    component: RowLink,
    row: row.original,
  };
};

type TopToolbarActionsProps = {
  table: MRTTableInstance<RowData>;
};

const TopToolbarActions: React.FC<TopToolbarActionsProps> = ({ table }) => {
  const isEmpty = table.getFilteredRowModel().rows.length === 0;
  const isLoading = table.getState()?.isLoading;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
      <Export disabled={isEmpty || isLoading} />
    </Box>
  );
};

const getVisibleColSizeMap = <TRow extends Record<string, unknown> = Record<string, unknown>>(
  table: MRTTableInstance<TRow>,
  containerEl?: HTMLElement | null
): Record<string, number> => {
  const ths = Array.from(containerEl?.querySelectorAll<HTMLElement>('thead th[data-col-id]') ?? []);

  if (ths.length) {
    const map = Object.fromEntries(
      ths
        .map(th => {
          const id = th.dataset.colId;
          return id ? [id, th.offsetWidth] : null;
        })
        .filter((p): p is [string, number] => Array.isArray(p))
    ) as Record<string, number>;
    return map;
  }

  const map = Object.fromEntries(
    table.getVisibleLeafColumns().map(c => [c.id, Math.round(c.getSize())])
  ) as Record<string, number>;
  return map;
};

const DataGrid: React.FC = () => {
  const { handler } = useDataGridFlexSales();
  const theme = useTheme();

  const enableRowExpansion = Boolean(handler.renderNestedComponent) || Boolean(handler.childMap);
  const enableVirtualization = !enableRowExpansion;
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const renderNestedComponent =
    (handler.renderNestedComponent as RenderNestedComponent) || renderNestedTable;

  const childMap = handler.childMap as ChildMap;

  const [data, setData] = useState<RowData[]>(() =>
    (handler.getRows() as CustomRow<RowData>[]).map(r => r.item)
  );

  const [loading, setLoading] = useState(handler.loading);

  const initialSorting = useMemo<MRTSortingState>(() => {
    const s = handler.sorting;
    return s.column ? [{ id: s.column.id, desc: !s.ascending }] : [];
  }, [handler.sorting]);

  const [sorting, setSorting] = useState<MRTSortingState>(initialSorting);

  useEffect(() => {
    const updateData = () => {
      const items = (handler.getRows() as CustomRow<RowData>[]).map(r => r.item);
      setData([...items]);
    };

    updateData();
    setLoading(handler.loading);

    const rowsListener = handler.addEventListener('rows', updateData);
    const sortingListener = handler.addEventListener('sorting', updateData);
    const loadingListener = handler.addEventListener('loading', ({ loading }) =>
      setLoading(loading)
    );

    return () => {
      rowsListener.dispose();
      sortingListener.dispose();
      loadingListener.dispose();
    };
  }, [handler]);

  const filterByColumn = (handler as any)?.filterByColumn as FilterByColumn | undefined;

  const filterValue = useSelector((state: any) => {
    if (!filterByColumn?.selector) return null;
    return filterByColumn.selector(state);
  });

  const filterAccessor = filterByColumn?.accessor;
  const displayData = useMemo(() => {
    if (!filterAccessor) return data;
    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return data;
    if (Array.isArray(filterValue)) {
      return data.filter(row => filterValue.includes(String(row?.[filterAccessor])));
    }
    return data.filter(row => String(row?.[filterAccessor]) === String(filterValue));
  }, [data, filterAccessor, filterValue]);

  const firstColLeftPaddingPx = enableRowExpansion ? 0 : NO_EXPAND_FIRST_COL_PADDING_PX;

  const columns = useMemo(
    () =>
      buildColumns(
        handler.columns,
        handler.actions,
        handler.context,
        theme.custom.tableHeaderFontSizePx,
        `${theme.typography.fontFamily}`,
        theme.custom.tableFontSizePx,
        firstColLeftPaddingPx
      ),
    [handler, handler.context, theme, firstColLeftPaddingPx]
  );

  const hiddenColumns = useMemo(
    () => new Set<string>(handler.columnsHidden),
    [handler.columnsHidden]
  );

  return (
    <Box
      data-guide="report-table-panel"
      sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}
    >
      <MaterialReactTable<RowData>
        enableHiding
        initialState={{
          columnVisibility: Object.fromEntries(
            Array.from(hiddenColumns).map(id => [id, false])
          ) as Record<string, boolean>,
          pagination: { pageIndex: 0, pageSize: PAGE_SIZE },
        }}
        enableTableFooter={false}
        columns={columns}
        data={displayData}
        enableSorting
        manualSorting
        enableDensityToggle={false}
        layoutMode="semantic"
        enableExpanding={enableRowExpansion}
        getRowCanExpand={() => enableRowExpansion}
        getRowId={row => `${row.conciliationHeaderId}_${row.store}_${row.accountingDate}`}
        renderDetailPanel={
          enableRowExpansion
            ? ({ row, table }) =>
                renderNestedComponent(
                  row,
                  columns,
                  hiddenColumns,
                  getVisibleColSizeMap(table, tableContainerRef.current),
                  childMap
                )
            : undefined
        }
        renderTopToolbarCustomActions={({ table }) => <TopToolbarActions table={table} />}
        onSortingChange={(updater: MRTUpdater<MRTSortingState>) =>
          setSorting((prev: MRTSortingState) => {
            const newSorting = typeof updater === 'function' ? updater(prev) : updater;
            const first = newSorting[0];
            if (first) handler.setSorting(first.id, !first.desc);
            else handler.setSorting(undefined);
            return newSorting;
          })
        }
        enableRowVirtualization={enableVirtualization}
        enablePagination={!loading}
        state={{ sorting, isLoading: loading }}
        renderEmptyRowsFallback={() => <VirtualTableNoDataCell />}
        muiDetailPanelProps={() => ({
          sx: {
            p: 0,
            m: 0,
            '& td': {
              backgroundColor: 'inherit',
              paddingTop: 1,
              paddingBottom: 1,
            },
            '& td[data-col-id="flexVentas"]': {
              pl: 2,
            },
          },
        })}
        muiTableBodyRowProps={({ row, isDetailPanel }) =>
          getBodyRowProps(row, Boolean(isDetailPanel), hiddenColumns)
        }
        muiTableBodyCellProps={({ row, column, table }) => ({
          sx: t => ({
            p: 0,
            pl:
              !enableRowExpansion && table.getVisibleLeafColumns()[0]?.id === column.id
                ? `${NO_EXPAND_FIRST_COL_PADDING_PX}px`
                : 0,
            backgroundColor: t.palette.background.paper,
            ...(row?.getIsExpanded?.() ? { borderBottom: 'none' } : {}),
            ...(column.id === 'store' && { fontWeight: 700 }),
            ...(column.id === 'xjust' && {
              '& .MuiBox-root': {
                overflow: 'visible !important',
              },
            }),
          }),
        })}
        enableStickyHeader
        muiTableContainerProps={{
          sx: {
            flexGrow: 1,
            backgroundColor: theme => theme.palette.background.paper,
            borderRadius: 0,
          },
          ref: tableContainerRef,
        }}
        muiTableHeadCellProps={({ column, table }) => ({
          'data-col-id': column.id,
          sx: {
            backgroundColor: t => t.custom.tableHeaderBackgroundColor,
            fontSize: theme.custom.tableHeaderFontSizePx,
            padding: '12px 0px 9.6px',
            pl:
              !enableRowExpansion && table.getVisibleLeafColumns()[0]?.id === column.id
                ? `${NO_EXPAND_FIRST_COL_PADDING_PX}px`
                : 0,
          },
        })}
        muiTopToolbarProps={{
          sx: { backgroundColor: theme => theme.palette.background.default, flexShrink: 0 },
        }}
        muiTablePaperProps={{
          elevation: 0,
          sx: {
            mt: '3px',
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
            boxShadow: theme => `0px 8px 6px -6px ${alpha(theme.palette.common.black, 0.2)}`,
          },
        }}
        muiBottomToolbarProps={{
          sx: {
            p: 0,
            minHeight: '4rem',
            '& .MuiTablePagination-root': { display: 'none' },
          },
        }}
        renderBottomToolbarCustomActions={({ table }) => (
          <TablePaginationBar table={table} pageSizeOptions={[10, 25, 50, 100]} />
        )}
        displayColumnDefOptions={{
          'mrt-row-expand': {
            size: 28,
            minSize: 28,

            muiTableBodyCellProps: ({ row }: { row: MRTRow<RowData> }) => ({
              sx: {
                backgroundColor: 'inherit',
                color: t => t.palette.text.primary,
                '& svg': { color: t => t.palette.primary.main },
                '& button:hover svg': { color: t => t.palette.info.light },
                ...(row.getIsExpanded() ? { borderBottom: 'none' } : {}),
              },
            }),
          },
        }}
      />
    </Box>
  );
};

export default DataGrid;
